/**
 * Key Bindings Settings Component
 * Allows users to customize keyboard controls for the DS emulator
 */

import { useState, useEffect } from 'react';
import { DSButton } from '../emulator/EmulatorCore';
import {
  DEFAULT_KEY_MAPPING,
  KeyMapping,
  loadKeyMapping,
  saveKeyMapping,
} from '../emulator/InputManager';
import KeyCaptureButton from './KeyCaptureButton';
import '../styles/key-bindings.css';

interface KeyBindingsSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

// Define button groups for organized display
const BUTTON_GROUPS = [
  {
    title: 'D-Pad',
    buttons: [
      { button: DSButton.Up, label: 'Up' },
      { button: DSButton.Down, label: 'Down' },
      { button: DSButton.Left, label: 'Left' },
      { button: DSButton.Right, label: 'Right' },
    ],
  },
  {
    title: 'Face Buttons',
    buttons: [
      { button: DSButton.A, label: 'A Button' },
      { button: DSButton.B, label: 'B Button' },
      { button: DSButton.X, label: 'X Button' },
      { button: DSButton.Y, label: 'Y Button' },
    ],
  },
  {
    title: 'Shoulder Buttons',
    buttons: [
      { button: DSButton.L, label: 'L Button' },
      { button: DSButton.R, label: 'R Button' },
    ],
  },
  {
    title: 'System',
    buttons: [
      { button: DSButton.Start, label: 'Start' },
      { button: DSButton.Select, label: 'Select' },
    ],
  },
];

export default function KeyBindingsSettings({ isOpen, onClose }: KeyBindingsSettingsProps) {
  const [keyMapping, setKeyMapping] = useState<KeyMapping>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [conflicts, setConflicts] = useState<Set<string>>(new Set());

  // Load key mappings on mount
  useEffect(() => {
    const mapping = loadKeyMapping();
    setKeyMapping(mapping);
  }, [isOpen]);

  // Get the key code for a specific button
  const getKeyForButton = (button: DSButton): string | null => {
    for (const [key, mappedButton] of Object.entries(keyMapping)) {
      if (mappedButton === button) {
        return key;
      }
    }
    return null;
  };

  // Check for key conflicts
  const checkConflicts = (newMapping: KeyMapping): Set<string> => {
    const conflicts = new Set<string>();
    const keyCounts = new Map<string, number>();

    // Count occurrences of each key
    for (const key of Object.keys(newMapping)) {
      keyCounts.set(key, (keyCounts.get(key) || 0) + 1);
    }

    // Mark keys that appear more than once
    for (const [key, count] of keyCounts.entries()) {
      if (count > 1) {
        conflicts.add(key);
      }
    }

    return conflicts;
  };

  // Handle key change
  const handleKeyChange = (button: DSButton, newKey: string) => {
    // Remove old mapping for this button
    const newMapping = { ...keyMapping };
    for (const key of Object.keys(newMapping)) {
      if (newMapping[key] === button) {
        delete newMapping[key];
      }
    }

    // Add new mapping
    newMapping[newKey] = button;

    setKeyMapping(newMapping);
    setHasChanges(true);
    setConflicts(checkConflicts(newMapping));
  };

  // Handle save
  const handleSave = () => {
    // Don't save if there are conflicts
    if (conflicts.size > 0) {
      alert('Please resolve key conflicts before saving.');
      return;
    }

    saveKeyMapping(keyMapping);
    setHasChanges(false);

    // Show success message
    const toast = document.createElement('div');
    toast.className = 'toast-success';
    toast.textContent = 'Key bindings saved successfully';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);

    onClose();
  };

  // Handle reset to defaults
  const handleReset = () => {
    if (window.confirm('Reset all key bindings to default values?')) {
      setKeyMapping({ ...DEFAULT_KEY_MAPPING });
      setHasChanges(true);
      setConflicts(new Set());
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Discard unsaved changes?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="key-bindings-overlay" onClick={handleCancel}>
      <div className="key-bindings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="key-bindings-header">
          <h2>Keyboard Controls</h2>
          <button
            className="close-button"
            onClick={handleCancel}
            aria-label="Close settings"
          >
            ✕
          </button>
        </div>

        <div className="key-bindings-content">
          <p className="key-bindings-description">
            Click on a button to capture a new key. Press the key you want to assign.
          </p>

          {conflicts.size > 0 && (
            <div className="conflict-warning">
              <strong>Warning:</strong> Some keys are assigned to multiple buttons.
              Please resolve conflicts before saving.
            </div>
          )}

          <div className="button-groups">
            {BUTTON_GROUPS.map((group) => (
              <div key={group.title} className="button-group">
                <h3 className="group-title">{group.title}</h3>
                <div className="button-mappings">
                  {group.buttons.map(({ button, label }) => {
                    const currentKey = getKeyForButton(button);
                    const hasConflict = currentKey ? conflicts.has(currentKey) : false;

                    return (
                      <div key={button} className="button-mapping">
                        <label className="button-label">{label}</label>
                        <KeyCaptureButton
                          currentKey={currentKey}
                          onKeyCapture={(key) => handleKeyChange(button, key)}
                          hasConflict={hasConflict}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="key-bindings-tips">
            <h4>Tips:</h4>
            <ul>
              <li>Avoid using browser shortcuts (F5, Ctrl+W, etc.)</li>
              <li>Arrow keys are great for D-pad controls</li>
              <li>Use keys close to each other for comfortable gameplay</li>
              <li>Press Escape during key capture to cancel</li>
            </ul>
          </div>
        </div>

        <div className="key-bindings-footer">
          <button className="btn btn-secondary" onClick={handleReset}>
            Reset to Defaults
          </button>
          <div className="footer-actions">
            <button className="btn btn-tertiary" onClick={handleCancel}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={conflicts.size > 0}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
